import {
  ATTR_ERROR_TYPE,
  ATTR_HTTP_REQUEST_METHOD,
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_SERVER_ADDRESS,
  ATTR_SERVER_PORT,
  METRIC_HTTP_CLIENT_REQUEST_DURATION,
} from '@opentelemetry/semantic-conventions';
import {
  ATTR_GRAPHQL_OPERATION_NAME,
  ATTR_GRAPHQL_OPERATION_TYPE,
  ATTR_URL_TEMPLATE,
} from '@opentelemetry/semantic-conventions/incubating';
import { getCommonAttributes, getMeter } from '../core/telemetrySdk';
import type { Attributes, Histogram } from '@opentelemetry/api';

/** Semconv default bucket boundaries for `http.client.request.duration` (seconds). */
export const HTTP_CLIENT_REQUEST_DURATION_BUCKETS = [
  0.005, 0.01, 0.025, 0.05, 0.075, 0.1, 0.25, 0.5, 0.75, 1, 2.5, 5, 7.5, 10,
];

/** Structural twin of the api package's request lifecycle start event; no import between the packages. */
export type HttpClientRequestStart = {
  method: string;
  path: string;
  urlTemplate: string;
  startedAt: number;
  /** Present for GraphQL requests; every operation shares the endpoint path. */
  graphqlOperation?: { name: string; type: 'query' | 'mutation' | 'subscription' };
};

export type HttpClientRequestOutcome = 'response' | 'network-error' | 'cancelled';

export type HttpClientRequestEnd = HttpClientRequestStart & {
  durationMs: number;
  outcome: HttpClientRequestOutcome;
  statusCode?: number;
  errorCode?: string;
};

export type HttpClientRequestContext = { attributes: Attributes };

export type HttpClientRequestRecorder = {
  onRequestStart: (start: HttpClientRequestStart) => HttpClientRequestContext;
  onRequestEnd: (end: HttpClientRequestEnd, context: HttpClientRequestContext) => void;
};

type RecorderOptions = {
  /**
   * Attributes snapshotted at request start (e.g. the current route). Defaults to the SDK common
   * attributes. Must not throw: the recorder calls it unguarded, so a throw propagates to whoever
   * invoked `onRequestStart`.
   */
  getAttributes?: () => Attributes;
};

let histogram: Histogram | null = null;
let serverAttributes: Attributes | null = null;

function getHistogram(): Histogram | null {
  if (!histogram) {
    histogram =
      getMeter()?.createHistogram(METRIC_HTTP_CLIENT_REQUEST_DURATION, {
        description: 'Duration of HTTP client requests observed from the browser',
        unit: 's',
        advice: { explicitBucketBoundaries: HTTP_CLIENT_REQUEST_DURATION_BUCKETS },
      }) ?? null;
  }
  return histogram;
}

/** `server.address` / `server.port` of the page origin; every same-origin request targets it. */
function getServerAttributes(): Attributes {
  if (!serverAttributes) {
    const location = globalThis.location;
    if (!location?.hostname) return {};
    const port = location.port ? Number(location.port) : location.protocol === 'https:' ? 443 : 80;
    serverAttributes = { [ATTR_SERVER_ADDRESS]: location.hostname, [ATTR_SERVER_PORT]: port };
  }
  return serverAttributes;
}

function requestAttributes(end: HttpClientRequestEnd): Attributes {
  const attributes: Attributes = {
    [ATTR_HTTP_REQUEST_METHOD]: end.method,
    [ATTR_URL_TEMPLATE]: end.urlTemplate,
    ...getServerAttributes(),
  };
  if (end.statusCode !== undefined) {
    attributes[ATTR_HTTP_RESPONSE_STATUS_CODE] = end.statusCode;
    if (end.statusCode >= 400) attributes[ATTR_ERROR_TYPE] = String(end.statusCode);
  } else if (end.outcome === 'network-error') {
    attributes[ATTR_ERROR_TYPE] = end.errorCode ?? 'network-error';
  }
  if (end.graphqlOperation) {
    attributes[ATTR_GRAPHQL_OPERATION_NAME] = end.graphqlOperation.name;
    attributes[ATTR_GRAPHQL_OPERATION_TYPE] = end.graphqlOperation.type;
  }
  return attributes;
}

/**
 * Builds a recorder that turns request lifecycle events into `http.client.request.duration`.
 * Subscribe it to the api package's request lifecycle seam. Safe to create before the SDK is
 * initialized: the instrument is created lazily and events before init are dropped.
 */
export function createHttpClientRequestRecorder(options: RecorderOptions = {}): HttpClientRequestRecorder {
  const getAttributes = options.getAttributes ?? getCommonAttributes;
  return {
    onRequestStart() {
      return { attributes: { ...getAttributes() } };
    },
    onRequestEnd(end, context) {
      if (end.outcome === 'cancelled') return;
      const instrument = getHistogram();
      if (!instrument) return;
      // Per-request attributes are spread last on purpose: they win over the start snapshot, so a
      // caller-supplied `getAttributes` can never shadow the semconv keys this recorder owns.
      instrument.record(end.durationMs / 1000, { ...context.attributes, ...requestAttributes(end) });
    },
  };
}

/** Test-only: drop the cached instrument and server attributes. */
export function resetHttpClientMetricsForTesting(): void {
  histogram = null;
  serverAttributes = null;
}
